import homeFill from '@iconify/icons-eva/home-fill';
import personFill from '@iconify/icons-eva/person-fill';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { Box, Divider, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { TUser, UserRole, UserStatus } from 'types/user';
import { getUserInfo, removeLocalStorage } from 'utils/utils';
import { MIconButton } from '../../components/@material-extend';
import LoadingAsyncButton from '../../components/LoadingAsyncButton/LoadingAsyncButton';
import MenuPopover from '../../components/MenuPopover';
import MyAvatar from '../../components/MyAvatar';

export default function AccountPopover() {
  const [userInfoFromLocalStorage, setUserInfoFromLocalStorage] = useState<TUser>({
    id: '',
    name: '',
    username: '',
    role: UserRole.StoreStaff,
    status: UserStatus.ACTIVE
  });

  const MENU_OPTIONS = [
    {
      label: 'Profile',
      icon: personFill,
      linkTo: PATH_DASHBOARD.user.profileById(userInfoFromLocalStorage?.id?.toString())
    },
    {
      label: 'Trang chủ',
      icon: homeFill,
      linkTo: '/'
    }
  ];

  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const { user, logout } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout?.();
      if (isMountedRef.current) {
        navigate('/', { replace: true });
        handleClose();
        removeLocalStorage('USER_INFO');
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  useEffect(() => {
    setUserInfoFromLocalStorage(JSON.parse(getUserInfo() ?? '{}'));
  }, []);

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24
              }}
            />

            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <LoadingAsyncButton
            fullWidth
            color="inherit"
            variant="outlined"
            onClick={() =>
              new Promise((res) =>
                setTimeout(() => {
                  res(handleLogout());
                }, 1000)
              )
            }
          >
            Đăng xuất
          </LoadingAsyncButton>
        </Box>
      </MenuPopover>
    </>
  );
}
